/**
 * AdminToggle — the ONE toggle used across every admin/prescriber
 * surface. Wraps the shadcn <Switch> (Radix under the hood) plus a
 * status badge so staff never second-guess whether something is on.
 *
 * Default labels are "Live" / "Hidden" because most admin toggles
 * flip visibility of a module. Pass `labels={{ on: "On", off: "Off" }}`
 * for setting-style toggles (e.g. "Pre-Screening", "Require ID").
 */
import { Switch } from "@/components/ui/switch";
import { Eye, EyeOff, Loader2 } from "lucide-react";

export default function AdminToggle({
  checked,
  onCheckedChange,
  disabled = false,
  busy = false,
  labels,
  showBadge = true,
  testId,
  ariaLabel,
  className = "",
}) {
  const on = labels?.on ?? "Live";
  const off = labels?.off ?? "Hidden";
  const isLiveHidden = on === "Live" && off === "Hidden";

  return (
    <span className={`inline-flex items-center gap-2 ${className}`}>
      <Switch
        checked={!!checked}
        disabled={disabled || busy}
        onCheckedChange={onCheckedChange}
        aria-label={ariaLabel}
        data-testid={testId}
        className="data-[state=checked]:bg-emerald-500 data-[state=unchecked]:bg-slate-300 dark:data-[state=unchecked]:bg-slate-700"
      />
      {showBadge ? (
        <span
          className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${
            checked
              ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300"
              : "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300"
          }`}
          data-testid={testId ? `${testId}-badge` : undefined}
        >
          {isLiveHidden ? (
            checked ? <><Eye className="w-3 h-3" /> {on}</> : <><EyeOff className="w-3 h-3" /> {off}</>
          ) : (
            checked ? on : off
          )}
        </span>
      ) : null}
      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400" /> : null}
    </span>
  );
}
